// The HUD (decision 0046: "per-frame HUD values write to the DOM directly, not through a re-render"; gate D2.1 counts
// renders). Preact draws the HUD's structure (which clusters and slots there are) only when that changes: on mount, a
// setting that adds or drops the minimap or the compass, a device change that moves the clusters. Every frame the game
// hands `update` the values, and direct writers put each one into its element, only when it changed, so a frame that
// changes nothing touches nothing. The HUD fades when idle and comes back on any change (decision 0046: "minimal and
// fading when idle"); the stamina bar shows while in use. Laid out at the safe areas' corners, sized from the
// viewport's short side and the text scale (phones play in landscape). Planted defect `UI_FAULTS.hudValueRender`
// routes the values through a Preact render instead, which the render counter must catch.
// The HUD is also a HUD panel slot (@rpg/contracts' HudPanelSlot, hud-panels.ts): a feature adds its own panel
// without the HUD importing it; top-left and top-center panels sit under the built-in clusters, the other anchors at
// their corners and edges.
// Its built-in clusters are parts the game picks (`HudStructure.parts`, IMP-0031): every one by default (the action-RPG
// HUD: health, stamina, weapon, level, objective, notice), only some (a farm game's objective line), or none, which
// leaves the panel slot alone for a game whose HUD is all its own panels. A part not drawn reads none of its values
// and needs none of its labels. Planted defects `UI_FAULTS.hudPartsIgnored` (every part drawn whatever the game
// picked) and `UI_FAULTS.hudNoDefaultParts` (a HUD that picks nothing loses the default).
import { h, render, type VNode } from "preact";
import type { HudAnchor, HudPanelSlot } from "@rpg/contracts";
import { createHudPanels } from "./hud-panels";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type El = VNode<any>;
import { UI_FAULTS } from "./faults";
import type { Translate } from "./app";

/** A text slot written straight to the DOM. */
export interface DirectText {
  set(text: string): void;
  /** DOM writes so far (the D2.1 check reads it beside the UI's render count). */
  writes(): number;
}

/** Write an element's text directly, only on change. */
export function directText(el: { textContent: string | null }): DirectText {
  let last: string | null = null;
  let n = 0;
  return {
    set(text) {
      if (text === last) return;
      last = text;
      el.textContent = text;
      n++;
    },
    writes: () => n,
  };
}

/** A 0..1 bar written as a CSS transform (no layout), only on change; `quantum` skips changes smaller than it. */
export function directBar(el: { style: { transform: string } }, quantum = 0.002): { set(fraction: number): void; writes(): number } {
  let last = Number.NaN;
  let n = 0;
  return {
    set(f) {
      const v = Math.min(1, Math.max(0, Number.isFinite(f) ? f : 0));
      if (Math.abs(v - last) < quantum) return;
      last = v;
      el.style.transform = `scaleX(${v})`;
      n++;
    },
    writes: () => n,
  };
}

/** A boolean attribute written directly, only on change. */
export function directFlag(el: { setAttribute(n: string, v: string): void; removeAttribute(n: string): void }, name: string): { set(on: boolean): void; writes(): number } {
  let last: boolean | null = null;
  let n = 0;
  return {
    set(on) {
      if (on === last) return;
      last = on;
      if (on) el.setAttribute(name, "");
      else el.removeAttribute(name);
      n++;
    },
    writes: () => n,
  };
}

/** When the HUD fades: idle after `after` s with no change, unless held awake (a fight). Pure; the HUD asks it each
 *  frame with whether anything changed. */
export function createIdleFader(after: number): { note(changed: boolean, now: number, awake?: boolean): boolean; touch(now: number): void } {
  let last = Number.NEGATIVE_INFINITY;
  return {
    note(changed, now, awake = false) {
      if (changed || awake || last === Number.NEGATIVE_INFINITY) last = now;
      return now - last >= after;
    },
    touch(now) {
      last = now;
    },
  };
}

/** A built-in cluster of the HUD. `health`, `stamina`, `weapon` and `level` are the combat stats, top-left;
 *  `objective` and `notice` sit top-center. */
export type HudPart = "health" | "stamina" | "weapon" | "level" | "objective" | "notice";

/** Every built-in part, in drawing order: the action-RPG HUD, and the default when a game picks none. */
export const HUD_PARTS: readonly HudPart[] = ["health", "stamina", "weapon", "level", "objective", "notice"];

/** The values the HUD shows, handed in every frame. Text values are string ids (translated once per change). Each
 *  part reads only its own fields (health: `hp`, `hpMax`; stamina: `stamina`, `staminaMax`; weapon: `weapon`,
 *  `cooldown`, `cooldownMax`; level: `level`, `xp`, `xpNext`; objective: `objective`), so a HUD without the combat
 *  parts is updated with `{}`. A drawn part's missing number reads as 0, its missing text as none. */
export interface HudValues {
  hp?: number;
  hpMax?: number;
  stamina?: number;
  staminaMax?: number;
  /** The equipped weapon's name (a string id), or null. */
  weapon?: string | null;
  /** Seconds left on the weapon's skill, and its full cooldown. */
  cooldown?: number;
  cooldownMax?: number;
  level?: number;
  /** Experience into this level, and what the next level needs. */
  xp?: number;
  xpNext?: number;
  /** The current objective (a string id), or null. */
  objective?: string | null;
  /** Hold the HUD awake (in a fight). */
  awake?: boolean;
}

/** Which slots the HUD has: structure changes (a Preact render), never per frame. */
export interface HudStructure {
  minimap: boolean;
  compass: boolean;
  /** The touch overlay is up: the clusters make room for its corner buttons. */
  touch: boolean;
  /** The built-in parts it draws, in any order (default every one, `HUD_PARTS`). `[]` with no minimap or compass
   *  leaves the panel slot alone. */
  parts?: readonly HudPart[];
}

/** The string ids the HUD formats. */
export interface HudLabels {
  /** The health meter's name. */
  health: string;
  stamina: string;
  /** "{hp} / {max}". */
  value: string;
  /** "Level {level}". */
  level: string;
  /** "{xp} / {next}". */
  xp: string;
  /** The skill is ready. */
  skillReady: string;
  /** "{seconds}" left on the skill. */
  cooldown: string;
  /** Before the objective's text ("Objective"). */
  objective: string;
}

/** The labels each part says things with (a part not drawn needs none of its labels). */
const PART_LABELS: Record<HudPart, readonly (keyof HudLabels)[]> = {
  health: ["health", "value"],
  stamina: ["stamina"],
  weapon: ["skillReady", "cooldown"],
  level: ["level", "xp"],
  objective: ["objective"],
  notice: [],
};

/** Options for {@link createHud}. */
export interface HudOptions {
  t: Translate;
  /** The string ids its parts say things with; only the drawn parts' labels are needed (a missing one throws). */
  labels?: Partial<HudLabels>;
  structure: HudStructure;
  /** Idle seconds before the HUD fades (default 4), and its faded opacity (default 0.3). */
  idle?: { after: number; opacity: number };
  /** Room the clusters leave for a touch overlay's corner buttons while it shows (css px): the top-left cluster moves
   *  right by `left`; the top-right one down by `top` and left by `right`, its minimap drawn `minimap` px across. */
  touchRoom?: { left: number; top: number; right: number; minimap: number };
  /** Where the HUD goes (default `document.body`). */
  container?: HTMLElement;
}

/** What the HUD shows now (for the handle and the specs). */
export interface HudState {
  visible: boolean;
  faded: boolean;
  staminaShown: boolean;
  texts: Record<string, string>;
  bars: Record<string, number>;
  structure: HudStructure;
}

/** The HUD. It is also the HUD panel slot: `addPanel` and `panels`. */
export interface Hud extends HudPanelSlot<HTMLElement> {
  readonly root: HTMLElement;
  /** Write this frame's values (direct DOM writes, only what changed). `now` in seconds. */
  update(v: HudValues, now: number): void;
  /** Show a notice (already translated) for `seconds` from the last update (a place found, an item gained). */
  notify(text: string, seconds: number): void;
  /** Change the structure (a Preact render, counted). */
  setStructure(s: Partial<HudStructure>): void;
  /** A slot's element ("minimap", "compass"), or null when the structure has none. */
  slot(name: "minimap" | "compass"): HTMLElement | null;
  /** Called after every structure render (a game re-attaches what it put in the slots). */
  onStructure(fn: () => void): () => void;
  /** Show or hide the whole HUD (hidden under screens and outside play). */
  setVisible(on: boolean): void;
  setTextScale(scale: number): void;
  setReduceMotion(on: boolean): void;
  /** Preact renders of the HUD so far (structure only; gate D2.1 reads it). */
  renders(): number;
  /** Direct DOM writes so far. */
  writes(): number;
  state(): HudState;
  dispose(): void;
}

const CSS = `
.rpg-hud{position:fixed;inset:0;pointer-events:none;z-index:8;color:#fbf6e8;font-family:var(--rpg-font,system-ui,sans-serif);
  --hud-u:calc(clamp(12.5px,2.6vmin,17px)*var(--rpg-text-scale,1));font-size:var(--hud-u);
  --hud-top:max(10px,env(safe-area-inset-top,0px));--hud-left:max(12px,env(safe-area-inset-left,0px));--hud-right:max(12px,env(safe-area-inset-right,0px));
  text-shadow:0 1px 2px rgba(0,0,0,.85)}
.rpg-hud[hidden]{display:none}
.rpg-hud-fade{transition:opacity .6s ease}
.rpg-hud[data-reduce-motion] .rpg-hud-fade{transition:none}
.rpg-hud[data-faded] .rpg-hud-fade{opacity:var(--hud-idle,.3)}
.rpg-hud-tl{position:absolute;left:var(--hud-left);top:var(--hud-top);display:flex;flex-direction:column;gap:.45em;min-width:13em}
.rpg-hud[data-touch] .rpg-hud-tl{left:calc(var(--hud-left) + var(--hud-touch-left,64px))}
.rpg-hud-tc{position:absolute;left:50%;top:var(--hud-top);transform:translateX(-50%);display:flex;flex-direction:column;align-items:center;gap:.4em;max-width:min(52vw,34em)}
.rpg-hud-tr{position:absolute;right:var(--hud-right);top:var(--hud-top)}
.rpg-hud[data-touch] .rpg-hud-tr{top:calc(var(--hud-top) + var(--hud-touch-top,72px));right:calc(var(--hud-right) + var(--hud-touch-right,0px))}
.rpg-hud[data-touch] .rpg-hud-tr canvas{width:var(--hud-touch-minimap,148px)!important;height:var(--hud-touch-minimap,148px)!important}
.rpg-hud-row{display:flex;align-items:center;gap:.5em}
.rpg-hud-cap{font-weight:700;font-size:.8em;letter-spacing:.04em;min-width:4.6em}
.rpg-hud-meter{position:relative;width:11em;height:.8em;border-radius:.4em;background:rgba(18,22,16,.72);box-shadow:0 0 0 1.5px rgba(0,0,0,.45);overflow:hidden}
@media (max-width:920px){.rpg-hud-meter{width:8em}.rpg-hud-cap{min-width:3.6em}.rpg-hud-meter[data-hud=xp]{width:6.5em}}
.rpg-hud-meter>i{position:absolute;inset:0;transform-origin:left center;border-radius:inherit}
.rpg-hud-meter[data-hud=hp]>i{background:linear-gradient(#f07a5f,#c94b37)}
.rpg-hud-meter[data-hud=stamina]>i{background:linear-gradient(#cfe88a,#8fbf4a)}
.rpg-hud-meter[data-hud=xp]{height:.45em;width:9em}
.rpg-hud-meter[data-hud=xp]>i{background:#e9c46a}
.rpg-hud-num{font-weight:600;font-size:.85em;font-variant-numeric:tabular-nums}
.rpg-hud-stamina:not([data-shown]){visibility:hidden}
.rpg-hud-weapon{display:flex;align-items:center;gap:.55em;font-weight:700}
.rpg-hud-ring{width:2em;height:2em}
.rpg-hud-ring circle{fill:none;stroke-width:4}
.rpg-hud-ring .bg{stroke:rgba(18,22,16,.7)}
.rpg-hud-ring .fg{stroke:#9fd8ff;transform:rotate(-90deg);transform-origin:50% 50%}
.rpg-hud-weapon[data-ready] .rpg-hud-ring .fg{stroke:#e9c46a}
.rpg-hud-obj{display:flex;align-items:center;gap:.5em;padding:.3em .8em;border-radius:1em;background:rgba(18,22,16,.55);font-weight:600}
.rpg-hud-obj:not([data-shown]){display:none}
.rpg-hud-obj b{color:#e9c46a;font-size:.8em;letter-spacing:.05em}
.rpg-hud-notice{padding:.3em .9em;border-radius:1em;background:rgba(233,196,106,.94);color:#1d2a1f;font-weight:700;text-shadow:none}
.rpg-hud-notice:not([data-shown]){display:none}
.rpg-hud-panels{display:flex;flex-direction:column;gap:.45em}
.rpg-hud>.rpg-hud-panels{position:absolute;max-width:min(40vw,24em)}
.rpg-hud>[data-hud-anchor=top-left]{left:var(--hud-left);top:var(--hud-top)}
.rpg-hud>[data-hud-anchor=top-center]{left:50%;top:var(--hud-top);transform:translateX(-50%);align-items:center}
.rpg-hud>[data-hud-anchor=top-right]{right:var(--hud-right);top:var(--hud-top);align-items:flex-end}
.rpg-hud>[data-hud-anchor=center-left]{left:var(--hud-left);top:50%;transform:translateY(-50%)}
.rpg-hud>[data-hud-anchor=center-right]{right:var(--hud-right);top:50%;transform:translateY(-50%);align-items:flex-end}
.rpg-hud>[data-hud-anchor=bottom-left]{left:var(--hud-left);bottom:max(12px,env(safe-area-inset-bottom,0px));flex-direction:column-reverse}
.rpg-hud>[data-hud-anchor=bottom-center]{left:50%;bottom:max(12px,env(safe-area-inset-bottom,0px));transform:translateX(-50%);flex-direction:column-reverse;align-items:center}
.rpg-hud>[data-hud-anchor=bottom-right]{right:var(--hud-right);bottom:max(12px,env(safe-area-inset-bottom,0px));flex-direction:column-reverse;align-items:flex-end}
`;

const RING_C = 2 * Math.PI * 15;

let styledDocs = new WeakSet<Document>();
function injectHudStyle(doc: Document): void {
  if (styledDocs.has(doc) || !doc.head) return;
  styledDocs.add(doc);
  const s = doc.createElement("style");
  s.dataset.rpgHud = "";
  s.textContent = CSS;
  doc.head.appendChild(s);
}

/** The parts a structure draws (the planted defects change the answer). */
function drawnParts(s: HudStructure): ReadonlySet<HudPart> {
  if (UI_FAULTS.hudPartsIgnored) return new Set(HUD_PARTS);
  return new Set(s.parts ?? (UI_FAULTS.hudNoDefaultParts ? [] : HUD_PARTS));
}

/** Every label the structure's parts need is there (throws naming the first missing one). */
function checkLabels(s: HudStructure, labels: Partial<HudLabels>): void {
  for (const part of s.parts ?? HUD_PARTS)
    for (const k of PART_LABELS[part]) if (typeof labels[k] !== "string") throw new Error(`hud: part "${part}" needs the label "${k}"`);
}

/** The HUD's structure as Preact draws it. With `values` (the planted defect only) the texts are drawn in too. */
function HudView(p: { s: HudStructure; t: Translate; labels: Partial<HudLabels>; values?: { texts: Record<string, string>; bars: Record<string, number> } }): El {
  const { labels } = p;
  const t = (k: string | undefined): string => p.t(k ?? "");
  const has = drawnParts(p.s);
  const tx = (k: string): string => p.values?.texts[k] ?? "";
  const bar = (k: string) => (p.values ? { style: `transform:scaleX(${p.values.bars[k] ?? 0})` } : {});
  const topLeft = has.has("health") || has.has("stamina") || has.has("weapon") || has.has("level");
  const topCenter = has.has("objective") || has.has("notice") || p.s.compass;
  return h(
    "div",
    { style: "display:contents" },
    topLeft
      ? h(
          "div",
          { class: "rpg-hud-tl rpg-hud-fade" },
          has.has("health")
            ? h(
                "div",
                { class: "rpg-hud-row" },
                h("span", { class: "rpg-hud-cap" }, t(labels.health)),
                h("span", { class: "rpg-hud-meter", "data-hud": "hp" }, h("i", bar("hp"))),
                h("span", { class: "rpg-hud-num", "data-hud": "hp-text" }, tx("hp-text")),
              )
            : null,
          has.has("stamina") ? h("div", { class: "rpg-hud-row rpg-hud-stamina", "data-hud": "stamina-row" }, h("span", { class: "rpg-hud-cap" }, t(labels.stamina)), h("span", { class: "rpg-hud-meter", "data-hud": "stamina" }, h("i", bar("stamina")))) : null,
          has.has("weapon")
            ? h(
                "div",
                { class: "rpg-hud-weapon", "data-hud": "weapon-row" },
                h("svg", { class: "rpg-hud-ring", viewBox: "0 0 36 36", "aria-hidden": "true" }, h("circle", { class: "bg", cx: 18, cy: 18, r: 15 }), h("circle", { class: "fg", cx: 18, cy: 18, r: 15, "data-hud": "cooldown", "stroke-dasharray": RING_C.toFixed(2) })),
                h("span", { "data-hud": "weapon" }, tx("weapon")),
                h("span", { class: "rpg-hud-num", "data-hud": "cooldown-text" }, tx("cooldown-text")),
              )
            : null,
          has.has("level") ? h("div", { class: "rpg-hud-row" }, h("span", { class: "rpg-hud-cap", "data-hud": "level" }, tx("level")), h("span", { class: "rpg-hud-meter", "data-hud": "xp" }, h("i", bar("xp"))), h("span", { class: "rpg-hud-num", "data-hud": "xp-text" }, tx("xp-text"))) : null,
        )
      : null,
    topCenter
      ? h(
          "div",
          { class: "rpg-hud-tc" },
          has.has("objective") ? h("div", { class: "rpg-hud-obj rpg-hud-fade", "data-hud": "objective-row" }, h("b", null, t(labels.objective)), h("span", { "data-hud": "objective" }, tx("objective"))) : null,
          p.s.compass ? h("div", { class: "rpg-hud-slot", "data-hud-slot": "compass" }) : null,
          has.has("notice") ? h("div", { class: "rpg-hud-notice", "data-hud": "notice" }, tx("notice")) : null,
        )
      : null,
    p.s.minimap ? h("div", { class: "rpg-hud-tr" }, h("div", { class: "rpg-hud-slot", "data-hud-slot": "minimap" })) : null,
  );
}

/** Build the HUD and mount it. */
export function createHud(o: HudOptions): Hud {
  const labels: Partial<HudLabels> = o.labels ?? {};
  checkLabels(o.structure, labels);
  const doc = (o.container ?? document.body).ownerDocument;
  injectHudStyle(doc);
  const root = doc.createElement("div");
  root.className = "rpg-hud";
  root.dataset.hud = "";
  // gameplay isn't screen-reader playable (decision 0063); the HUD is a picture of the play state
  root.setAttribute("aria-hidden", "true");
  (o.container ?? doc.body).appendChild(root);
  // Preact draws into `view`; panel containers for the corner anchors sit beside it in `root`, outside Preact's tree
  const view = doc.createElement("div");
  view.style.display = "contents";
  root.appendChild(view);
  const panels = createHudPanels<HTMLElement>({
    create: () => doc.createElement("div"),
    parentFor: (anchor: HudAnchor) => (anchor === "top-left" ? root.querySelector<HTMLElement>(".rpg-hud-tl") : anchor === "top-center" ? root.querySelector<HTMLElement>(".rpg-hud-tc") : anchor === "top-right" ? root.querySelector<HTMLElement>(".rpg-hud-tr") : null) ?? root,
  });
  let reduceMotion = false;
  const idle = o.idle ?? { after: 4, opacity: 0.3 };
  root.style.setProperty("--hud-idle", String(idle.opacity));
  if (o.touchRoom) {
    root.style.setProperty("--hud-touch-left", `${o.touchRoom.left}px`);
    root.style.setProperty("--hud-touch-top", `${o.touchRoom.top}px`);
    root.style.setProperty("--hud-touch-right", `${o.touchRoom.right}px`);
    root.style.setProperty("--hud-touch-minimap", `${o.touchRoom.minimap}px`);
  }
  let structure: HudStructure = { ...o.structure };
  let has = drawnParts(structure);
  let renders = 0;
  let writes = 0;
  const listeners: (() => void)[] = [];
  const fader = createIdleFader(idle.after);
  let faded = false;
  let staminaShown = false;
  const texts: Record<string, string> = {};
  const bars: Record<string, number> = {};
  let visible = true;
  let notice: { text: string; until: number } | null = null;
  let lastNow = 0;

  type W = { set(v: string): void; writes(): number };
  let writers: Record<string, W> = {};
  let barWriters: Record<string, { set(f: number): void; writes(): number }> = {};
  let flags: Record<string, { set(on: boolean): void; writes(): number }> = {};
  let ringEl: SVGElement | null = null;
  let ringLast = Number.NaN;
  const fadedFlag = directFlag(root, "data-faded");

  const q = (k: string): HTMLElement => {
    const el = root.querySelector<HTMLElement>(`[data-hud="${k}"]`);
    if (!el) throw new Error(`hud: no element "${k}"`);
    return el;
  };
  // each drawn part's writers (texts, bars, flags: [name, element, attribute]), bound after every structure render
  const PART_TEXTS: Record<HudPart, readonly string[]> = { health: ["hp-text"], stamina: [], weapon: ["weapon", "cooldown-text"], level: ["level", "xp-text"], objective: ["objective"], notice: ["notice"] };
  const PART_BARS: Record<HudPart, readonly string[]> = { health: ["hp"], stamina: ["stamina"], weapon: [], level: ["xp"], objective: [], notice: [] };
  const PART_FLAGS: Record<HudPart, readonly (readonly [string, string, string])[]> = { health: [], stamina: [["stamina", "stamina-row", "data-shown"]], weapon: [["ready", "weapon-row", "data-ready"]], level: [], objective: [["objective", "objective-row", "data-shown"]], notice: [["notice", "notice", "data-shown"]] };
  const bind = (): void => {
    const counted = (w: W): W => ({ set: (v) => w.set(v), writes: w.writes });
    const drawn = HUD_PARTS.filter((p) => has.has(p));
    writers = {};
    for (const k of ["hp-text", "weapon", "cooldown-text", "level", "xp-text", "objective", "notice"]) if (drawn.some((p) => PART_TEXTS[p].includes(k))) writers[k] = counted(directText(q(k)));
    barWriters = {};
    for (const k of ["hp", "stamina", "xp"]) if (drawn.some((p) => PART_BARS[p].includes(k))) barWriters[k] = directBar(q(k).firstElementChild as HTMLElement);
    flags = {};
    for (const p of drawn) for (const [name, el, attr] of PART_FLAGS[p]) flags[name] = directFlag(q(el), attr);
    ringEl = has.has("weapon") ? root.querySelector<SVGElement>('[data-hud="cooldown"]') : null;
    ringLast = Number.NaN;
    for (const k of Object.keys(texts)) delete texts[k];
    for (const k of Object.keys(bars)) delete bars[k];
  };
  const totalWrites = (): number => writes + fadedFlag.writes() + [...Object.values(writers), ...Object.values(barWriters), ...Object.values(flags)].reduce((n, w) => n + w.writes(), 0);
  let baseWrites = 0;

  const draw = (values?: { texts: Record<string, string>; bars: Record<string, number> }): void => {
    // writes made by the old writers stay counted
    baseWrites = totalWrites();
    has = drawnParts(structure);
    render(h(HudView, { s: structure, t: o.t, labels, ...(values ? { values } : {}) }), view);
    renders++;
    root.toggleAttribute("data-touch", structure.touch);
    if (!values) {
      bind();
      writes = baseWrites - fadedFlag.writes();
      panels.reattach();
      for (const fn of listeners) fn();
    }
  };
  draw();

  // the formatted texts, cached by their inputs (a string is formatted only when its numbers change)
  const memo = new Map<string, { key: string; text: string }>();
  const fmt = (slot: string, key: string, make: () => string): string => {
    const m = memo.get(slot);
    if (m && m.key === key) return m.text;
    const text = make();
    memo.set(slot, { key, text });
    return text;
  };

  const hud: Hud = {
    root,
    update(v, now) {
      // only the drawn parts' values are read (a part not drawn needs none of its fields)
      const hpNow = v.hp ?? 0;
      const hpMax = v.hpMax ?? 0;
      const stamina = v.stamina ?? 0;
      const staminaMax = v.staminaMax ?? 0;
      const cooldown = v.cooldown ?? 0;
      const level = v.level ?? 0;
      const xp = v.xp ?? 0;
      const xpNext = v.xpNext ?? 0;
      const L = labels as HudLabels;
      const next: Record<string, string> = {};
      if (has.has("health")) {
        const hp = Math.max(0, Math.ceil(hpNow));
        next["hp-text"] = fmt("hp", `${hp}/${hpMax}`, () => o.t(L.value, { hp, max: Math.round(hpMax) }));
      }
      if (has.has("weapon")) {
        next.weapon = fmt("weapon", v.weapon ?? "", () => (v.weapon ? o.t(v.weapon) : ""));
        next["cooldown-text"] = cooldown > 0.05 ? fmt("cd", String(Math.ceil(cooldown)), () => o.t(L.cooldown, { seconds: Math.ceil(cooldown) })) : fmt("cd", "ready", () => o.t(L.skillReady));
      }
      if (has.has("level")) {
        next.level = fmt("level", String(level), () => o.t(L.level, { level }));
        next["xp-text"] = fmt("xp", `${Math.floor(xp)}/${xpNext}`, () => o.t(L.xp, { xp: Math.floor(xp), next: xpNext }));
      }
      if (has.has("objective")) next.objective = fmt("objective", v.objective ?? "", () => (v.objective ? o.t(v.objective) : ""));
      if (has.has("notice")) next.notice = notice && now < notice.until ? notice.text : "";
      lastNow = now;
      const nextBars: Record<string, number> = {};
      if (has.has("health")) nextBars.hp = hpMax > 0 ? hpNow / hpMax : 0;
      if (has.has("stamina")) nextBars.stamina = staminaMax > 0 ? stamina / staminaMax : 0;
      if (has.has("level")) nextBars.xp = xpNext > 0 ? xp / xpNext : 0;
      let changed = false;
      for (const [k, s] of Object.entries(next)) if (texts[k] !== s) changed = true;
      for (const [k, f] of Object.entries(nextBars)) if (bars[k] === undefined || Math.abs(bars[k]! - f) >= 0.002) changed = true;
      const nowFaded = fader.note(changed, now, v.awake === true || panels.awake());
      // the stamina bar shows while it is in use (below full)
      const showStamina = has.has("stamina") && staminaMax > 0 && stamina < staminaMax - 0.5;
      if (UI_FAULTS.hudValueRender) {
        // the planted defect: the values go through a Preact render, every frame
        Object.assign(texts, next);
        Object.assign(bars, nextBars);
        draw({ texts: { ...next }, bars: { ...nextBars } });
      } else {
        for (const [k, s] of Object.entries(next)) {
          if (texts[k] === s) continue;
          texts[k] = s;
          writers[k]!.set(s);
        }
        for (const [k, f] of Object.entries(nextBars)) {
          if (bars[k] !== undefined && Math.abs(bars[k]! - f) < 0.002) continue;
          bars[k] = f;
          barWriters[k]!.set(f);
        }
        flags.stamina?.set(showStamina);
        flags.objective?.set(!!v.objective);
        flags.ready?.set(cooldown <= 0.05);
        flags.notice?.set(next.notice !== "");
        const cooldownMax = v.cooldownMax ?? 0;
        const ring = cooldownMax > 0 ? 1 - Math.min(1, Math.max(0, cooldown / cooldownMax)) : 1;
        if (ringEl && !(Math.abs(ring - ringLast) < 0.005)) {
          ringEl.style.strokeDashoffset = ((1 - ring) * RING_C).toFixed(2);
          ringLast = ring;
          writes++;
        }
      }
      staminaShown = showStamina;
      if (nowFaded !== faded) faded = nowFaded;
      fadedFlag.set(faded);
      panels.update({ now, visible, reduceMotion });
    },
    notify(text, seconds) {
      notice = { text, until: lastNow + seconds };
    },
    setStructure(s) {
      const nextS = { ...structure, ...s };
      const sameParts = (a: readonly HudPart[] | undefined, b: readonly HudPart[] | undefined): boolean => a === b || (!!a && !!b && (UI_FAULTS.hudPartsOneWay ? a.length === b.length : b.every((p) => a.includes(p))) && a.every((p) => b.includes(p)));
      if (nextS.minimap === structure.minimap && nextS.compass === structure.compass && nextS.touch === structure.touch && sameParts(nextS.parts, structure.parts)) return;
      checkLabels(nextS, labels);
      structure = nextS;
      draw();
    },
    slot: (name) => root.querySelector<HTMLElement>(`[data-hud-slot="${name}"]`),
    onStructure(fn) {
      listeners.push(fn);
      return () => {
        const i = listeners.indexOf(fn);
        if (i >= 0) listeners.splice(i, 1);
      };
    },
    setVisible(on) {
      if (on === visible) return;
      visible = on;
      if (on) root.removeAttribute("hidden");
      else root.setAttribute("hidden", "");
      // coming back shows the HUD awake
      if (on) fader.touch(Number.NEGATIVE_INFINITY);
    },
    setTextScale: (s) => root.style.setProperty("--rpg-text-scale", String(s)),
    setReduceMotion: (on) => {
      reduceMotion = on;
      root.toggleAttribute("data-reduce-motion", on);
    },
    addPanel: (panel) => panels.addPanel(panel),
    panels: () => panels.panels(),
    renders: () => renders,
    writes: () => totalWrites(),
    state: () => ({ visible, faded, staminaShown, texts: { ...texts }, bars: { ...bars }, structure: { ...structure, ...(structure.parts ? { parts: [...structure.parts] } : {}) } }),
    dispose() {
      panels.dispose();
      render(null, view);
      root.remove();
    },
  };
  return hud;
}

/** For tests: forget which documents have the style. */
export function resetHudStyle(): void {
  styledDocs = new WeakSet();
}
